import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Container,
  FormHelperText,
  CircularProgress,
  Button,
  Divider,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { Typography, TextField } from "@mui/material";
import clsx from "clsx";
import { useViewport } from "../../helpers";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { createMyDesign } from "../../reducers/myDesigns/myDesignsThunks";
import { closeCreateDesign } from "../../reducers/myDesigns/myDesignsSlice";
import { myDesignService } from "../../services";

const PREFIX = "CreateMyDesign";

const classes = {
  rootSize: `${PREFIX}-rootSize`,
  marginTop: `${PREFIX}-marginTop`,
  imageContainer: `${PREFIX}-imageContainer`,
  formControl: `${PREFIX}-formControl`,
  myDesignImageContainer: `${PREFIX}-myDesignImageContainer`,
  paintingContainer: `${PREFIX}-paintingContainer`,
  uploadButton: `${PREFIX}-uploadButton`,
  uploadField: `${PREFIX}-uploadField`,
  stepperButtonPositions: `${PREFIX}-stepperButtonPositions`,
  marginLeft: `${PREFIX}-marginLeft`,
  negateMarginTop: `${PREFIX}-negateMarginTop`,
  marginBottom: `${PREFIX}-marginBottom`,
};

const StyledContainer = styled(Container)(({ theme }) => ({
  [`& .${classes.rootSize}`]: {
    width: "350px",
  },

  [`& .${classes.marginTop}`]: {
    marginTop: "1rem",
  },

  [`& .${classes.imageContainer}`]: {
    marginTop: "1rem",
  },

  [`& .${classes.formControl}`]: {
    margin: theme.spacing(1),
    minWidth: 120,
  },

  [`& .${classes.myDesignImageContainer}`]: {
    width: "100%",
    maxWidth: "300px",
    objectFit: "cover",
    margin: "0",
  },

  [`& .${classes.paintingContainer}`]: {
    display: "flex",
    flexDirection: "column",
  },

  [`& .${classes.uploadButton}`]: {
    height: "50px",
    marginTop: "1rem",
  },

  [`& .${classes.uploadField}`]: {
    maxWidth: "600px",
    textAlign: "left",
    marginBottom: "1rem",
    display: "flex",
    flexDirection: "row",
  },

  [`& .${classes.stepperButtonPositions}`]: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    marginBottom: "1rem",
  },

  [`& .${classes.marginLeft}`]: {
    marginLeft: "1rem",
  },

  [`& .${classes.negateMarginTop}`]: {
    marginTop: "-0.5rem",
  },

  [`& .${classes.marginBottom}`]: {
    marginBottom: "1rem !important",
  },
}));

const CreateMyDesign = () => {
  const { enqueueSnackbar } = useSnackbar();
  const history = useNavigate();
  const { t } = useTranslation();
  const { width } = useViewport();
  const dispatch = useDispatch();
  const [confidentiality, setConfidentiality] = useState(false);
  const {
    register,
    watch,
    formState: { errors },
    trigger,
    getValues,
    control,
  } = useForm({
    mode: "onChange",
  });
  const watchMyDesignImage = watch("myDesignImage");
  const [url, setUrl] = useState(null);
  const [saving, setSaving] = useState(false);

  function handleImageReader(event) {
    let tmp = URL.createObjectURL(event.target.files[0]);
    setUrl(tmp);
  }

  async function handleSaveNext() {
    setSaving(true);
    const tmp = getValues();
    const file = tmp.hasOwnProperty("myDesignImage") ? tmp.myDesignImage : null;
    URL.revokeObjectURL(url);
    try {
      const thunk = await dispatch(
        createMyDesign({
          folderId: tmp.folder,
          designName: tmp.designName,
          confidentiality,
        })
      );
      if (thunk.meta.requestStatus === "rejected")
        throw new Error("recovery error");
      if (file.length > 0) {
        myDesignService
          .associateBorderlessImageMyDesign(file, thunk.payload.id)
          .then(() => {
            enqueueSnackbar(t("toasts.createMyDesignSuccess"), {
              variant: "success",
            });
            dispatch(closeCreateDesign());
            history(`/mydesign/${thunk.payload.id}`);
          })
          .catch((err) => {
            if (err === 403)
              enqueueSnackbar(t("toasts.unauthorizedError"), {
                variant: "error",
              });
            else
              enqueueSnackbar(t("toasts.associateBorderlessImageError"), {
                variant: "error",
              });
          });
      } else {
        enqueueSnackbar(t("toasts.createMyDesignSuccess"), {
          variant: "success",
        });
        dispatch(closeCreateDesign());
        history(`/mydesign/${thunk.payload.id}`);
      }
    } catch (err) {
      setSaving(false);
      enqueueSnackbar(t("toasts.createMyDesignError"), { variant: "error" });
    }
  }

  return (
    <StyledContainer>
      <div className={classes.rootSize}>
        <Typography
          variant="h6"
          className={clsx(classes.marginBottom, classes.marginTop)}
        >
          {t("homePage.myDesign.createForm.generalInformations")}
        </Typography>
        <TextField
          {...register("designName", {
            validate: (value) => (value.length > 3 ? null : "errorDesignName"),
          })}
          name="designName"
          label={t("homePage.myDesign.createForm.designName")}
          error={errors.hasOwnProperty("designName")}
          className={clsx(classes.marginBottom)}
          placeholder=""
          fullWidth
          variant={"outlined"}
        />
        {errors.hasOwnProperty("designName") && (
          <FormHelperText
            id="nameRequired"
            error
            className={clsx(classes.negateMarginTop, classes.marginBottom)}
          >
            {t("homePage.myDesign.createForm.errors.designName")}
          </FormHelperText>
        )}
        <TextField
          {...register("folder", {
            validate: (value) =>
              value.length > 0 ? null : "errorFolderNumber",
          })}
          name="folder"
          label={t("homePage.myDesign.createForm.folderId")}
          error={errors.hasOwnProperty("folder")}
          className={clsx(classes.marginBottom)}
          placeholder=""
          fullWidth
          variant={"outlined"}
        />
        {errors.hasOwnProperty("folder") && (
          <FormHelperText
            id="folderRequired"
            error
            className={clsx(classes.negateMarginTop, classes.marginBottom)}
          >
            {t("homePage.myDesign.createForm.errors.folder")}
          </FormHelperText>
        )}
        <FormControlLabel
          control={
            <Checkbox
              color={"primary"}
              onChange={(e) => setConfidentiality(e.target.checked)}
              checked={confidentiality}
            />
          }
          label={
            <Typography variant="body1">
              {t("homePage.myDesign.createForm.confidentiality")}
            </Typography>
          }
          className={classes.autoplayCheckbox}
        />
        <div className={classes.imageContainer}>
          <Typography variant="h6">
            {t("homePage.myDesign.createForm.choseImage")}
          </Typography>
          <div className={classes.uploadField}>
            <div className={classes.paintingContainer}>
              <img
                id="myDesignImage"
                className={classes.myDesignImageContainer}
                alt={watchMyDesignImage ? "myDesign image" : null}
                src={url ? url : undefined}
              />
              <div className={classes.positionContainerContent}>
                <Button
                  variant="contained"
                  color="primary"
                  component="label"
                  className={classes.uploadButton}
                  onChange={(e) => handleImageReader(e)}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  {t("myDesign.zones.uploadVideo.selectFileButton")}
                  <input
                    type="file"
                    accept="image/*"
                    name="myDesignImage"
                    {...register("myDesignImage")}
                    hidden
                  />
                </Button>
                <Typography variant="body1" className={classes.fileName}>
                  {watchMyDesignImage && watchMyDesignImage.length > 0
                    ? watchMyDesignImage[0].name
                    : ""}
                </Typography>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Divider light className={classes.marginBottom} />
      <div className={classes.stepperButtonPositions}>
        <Button
          color="primary"
          variant="contained"
          disabled={saving}
          onClick={async () => {
            const result = await trigger();
            if (result) {
              handleSaveNext();
            }
          }}
          className={clsx(classes.marginLeft)}
        >
          {saving ? (
            <CircularProgress />
          ) : (
            t("homePage.myDesign.createForm.create")
          )}
        </Button>
      </div>
    </StyledContainer>
  );
};

export default CreateMyDesign;
